<template>
    <div class="min-h-screen bg-auas-500 staff-index-page relative overflow-hidden">
        <!-- Animated Background Elements -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
            <div
                class="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-400/20 to-primary-600/20 rounded-full blur-3xl animate-float">
            </div>
            <div
                class="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-primary-400/20 to-primary-600/20 rounded-full blur-3xl animate-float-delayed">
            </div>
            <div
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-primary-400/10 to-primary-600/10 rounded-full blur-2xl animate-pulse-slow">
            </div>
        </div>

        <!-- Header -->
        <header class="relative z-10 py-12 from-auas-600 via-auas-700 to-auas-800"> <!-- bg-gradient-to-r -->
            <div class="absolute inset-x-0 top-4 z-20 px-4 sm:top-5 sm:px-6 lg:top-6 lg:px-8">
                <div class="mx-auto flex w-full max-w-[1600px] justify-end">
                    <a href="/submit/login"
                        class="inline-flex min-h-11 items-center justify-center rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white shadow-lg backdrop-blur-xl transition-all duration-300 hover:bg-white/20 hover:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-auas-600 sm:rounded-2xl sm:px-5 sm:py-3">
                        <span class="whitespace-nowrap">Submit Profile</span>
                    </a>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="pt-16 text-center sm:pt-14 lg:pt-12">
                    <div class="mb-8 relative z-50">
                        <a href="https://auas.edu.krd"
                            class="inline-block transition-transform hover:scale-105 duration-300">
                            <img :src="'/AUAS.png'" alt="AUAS Logo"
                                class="w-32 h-32 mx-auto object-contain border border-white/30 rounded-full p-0.05" />
                        </a>
                    </div>
                    <h1 class="text-5xl font-bold text-white mb-4">
                        Academic Staff Directory
                    </h1>
                    <p class="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
                        Discover our exceptional faculty members and their expertise
                    </p>
                    <div class="mt-6 flex items-center justify-center space-x-4 text-white/80">
                        <div class="flex items-center space-x-2">
                            <div class="w-2 h-2 bg-auas-400 rounded-full animate-pulse"></div>
                            <span class="text-sm text-white">{{ staff.total }} Active Members</span>
                        </div>
                        <div class="w-1 h-4 bg-white/30"></div>
                        <div class="flex items-center space-x-2">
                            <div class="w-2 h-2 bg-auas-400 rounded-full animate-pulse"></div>
                            <span class="text-sm text-white">{{ departments.length }} Departments</span>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- Advanced Search and Filters -->
        <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
            <div class="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 mb-8 overflow-hidden">
                <!-- Search Bar -->
                <div class="relative mb-6">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                    </div>
                    <input v-model="searchForm.search" type="text"
                        placeholder="Search by name, title, specialization, or email..."
                        class="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-auas-300/50 focus:bg-white/20 transition-all duration-300 text-lg text-white placeholder-white/60"
                        @input="debouncedSearch" />
                    <div v-if="isSearching" class="absolute inset-y-0 right-0 pr-4 flex items-center">
                        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                    </div>
                </div>

                <!-- Cascading Dropdown Filters -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <!-- College Dropdown -->
                    <div>
                        <label class="block text-sm font-medium text-white mb-2">Filter by College:</label>
                        <select v-model="searchForm.college" @change="onCollegeChange"
                            class="w-full border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-auas-300 focus:border-transparent bg-white/10 text-white shadow-sm">
                            <option value="">All Colleges ({{ staff.total }})</option>
                            <option v-for="college in colleges" :key="college.id" :value="college.id">
                                {{ college.name_en }} ({{ getCollegeStaffCount(college.id) }})
                            </option>
                        </select>
                    </div>

                    <!-- Department Dropdown -->
                    <div>
                        <label class="block text-sm font-medium text-white mb-2">Filter by Department:</label>
                        <select v-model="searchForm.department" @change="onDepartmentChange"
                            :disabled="!searchForm.college"
                            class="w-full border border-white/20 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-auas-300 focus:border-transparent bg-white/10 text-white shadow-sm disabled:bg-white/5 disabled:cursor-not-allowed">
                            <option value="">
                                {{ searchForm.college ? 'All Departments in Selected College' : 'Select a college first'
                                }}
                                {{ searchForm.college ? `(${getSelectedCollegeTotalStaff()})` : '' }}
                            </option>
                            <option v-if="searchForm.college" v-for="department in availableDepartments"
                                :key="department.id" :value="department.id">
                                {{ department.name_en }} ({{ department.active_staff_count || 0 }})
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Advanced Filters Toggle -->
                <div class="border-t border-white/20 pt-6">
                    <button @click="showAdvancedFilters = !showAdvancedFilters"
                        class="flex items-center space-x-2 text-white hover:text-white/80 transition-colors duration-200 font-medium">
                        <svg :class="['w-4 h-4 transition-transform duration-300', showAdvancedFilters ? 'rotate-180' : '']"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7">
                            </path>
                        </svg>
                        <span class="font-medium">Advanced Filters</span>
                    </button>

                    <div v-show="showAdvancedFilters" class="mt-4 space-y-6 animate-fade-in">
                        <!-- First Row: Title, Specialization, Sort -->
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <!-- Title Filter -->
                            <div>
                                <label class="block text-sm font-medium text-white mb-2">Academic Title</label>
                                <select v-model="searchForm.title"
                                    class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-auas-300/50 transition-all duration-300 text-white"
                                    @change="search">
                                    <option value="">All Titles</option>
                                    <option value="Professor">Professor</option>
                                    <option value="Associate Professor">Associate Professor</option>
                                    <option value="Assistant Professor">Assistant Professor</option>
                                    <option value="Lecturer">Lecturer</option>
                                    <option value="Senior Lecturer">Senior Lecturer</option>
                                </select>
                            </div>

                            <!-- Specialization Filter -->
                            <div>
                                <label class="block text-sm font-medium text-white mb-2">Specialization</label>
                                <input v-model="searchForm.specialization" type="text"
                                    placeholder="e.g. Computer Science, Mathematics"
                                    class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-auas-300/50 transition-all duration-300 text-white placeholder-white/60"
                                    @input="debouncedSearch" />
                            </div>

                            <!-- Sort Options -->
                            <div>
                                <label class="block text-sm font-medium text-white mb-2">Sort By</label>
                                <select v-model="searchForm.sort"
                                    class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-auas-300/50 transition-all duration-300 text-white"
                                    @change="search">

                                    <option value="rank">Highest degree/rank</option>
                                    <option value="name">Name (A-Z)</option>
                                    <option value="name_desc">Name (Z-A)</option>
                                    <option value="department">Department</option>
                                    <option value="title">Academic Title</option>
                                </select>
                            </div>
                        </div>

                        <!-- Second Row: Alphabetic Filter -->
                        <div>
                            <label class="block text-sm font-medium text-white mb-3">Filter by Name (A-Z)</label>
                            <div class="grid grid-cols-6 sm:grid-cols-9 md:grid-cols-13 gap-2">
                                <button v-for="letter in alphabet" :key="letter" @click="selectLetter(letter)" :class="[
                                    'relative h-10 w-10 rounded-lg text-sm font-semibold transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-auas-300 focus:ring-offset-2',
                                    searchForm.letter === letter
                                        ? 'bg-gradient-to-br from-auas-500 to-auas-600 text-white shadow-lg shadow-auas-500/25'
                                        : 'bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:border-white/30 hover:text-white shadow-sm'
                                ]">
                                    {{ letter }}
                                    <div v-if="searchForm.letter === letter"
                                        class="absolute -top-1 -right-1 w-3 h-3 bg-success-500 rounded-full border-2 border-white">
                                    </div>
                                </button>
                                <button @click="clearLetter" :class="[
                                    'h-10 w-20 rounded-lg text-xs font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-auas-300 focus:ring-offset-2',
                                    searchForm.letter
                                        ? 'bg-white/20 text-white hover:bg-white/30 border border-white/30'
                                        : 'bg-white/5 text-white/40 cursor-not-allowed border border-white/10'
                                ]" :disabled="!searchForm.letter">
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Active Filters Display -->
                <div v-if="hasActiveFilters" class="mt-6 flex flex-wrap items-center gap-2">
                    <span class="text-sm text-white/70">Active filters:</span>
                    <div v-if="searchForm.search"
                        class="inline-flex items-center bg-white/20 text-white px-3 py-1 rounded-full text-sm border border-white/30">
                        Search: "{{ searchForm.search }}"
                        <button @click="clearFilter('search')"
                            class="ml-2 hover:text-white/80 transition-colors duration-200">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    </div>
                    <div v-if="searchForm.letter"
                        class="inline-flex items-center bg-white/20 text-white px-3 py-1 rounded-full text-sm border border-white/30">
                        Letter: "{{ searchForm.letter }}"
                        <button @click="clearLetter" class="ml-2 hover:text-white/80 transition-colors duration-200">
                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    </div>
                    <button @click="clearAllFilters"
                        class="text-sm text-white hover:text-white/80 font-medium transition-colors duration-200">
                        Clear all filters
                    </button>
                </div>
            </div>

            <!-- Results Summary -->
            <div class="mb-8 flex items-center justify-between">
                <div class="flex items-center space-x-4">
                    <h2 class="text-2xl font-bold text-white">
                        {{ staff.data.length }} {{ staff.data.length === 1 ? 'Staff Member' : 'Staff Members' }}
                    </h2>
                    <div v-if="staff.data.length > 0" class="text-sm text-white/70">
                        Showing {{ staff.from }}-{{ staff.to }} of {{ staff.total }} results
                    </div>
                </div>
                <div class="flex items-center space-x-2">
                    <button @click="viewMode = 'grid'" :class="[
                        'p-2 rounded-lg transition-all duration-200',
                        viewMode === 'grid' ? 'bg-white/20 text-white ring-1 ring-white/30' : 'text-white/60 hover:text-white hover:bg-white/10'
                    ]">
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z">
                            </path>
                        </svg>
                    </button>
                    <button @click="viewMode = 'list'" :class="[
                        'p-2 rounded-lg transition-all duration-200',
                        viewMode === 'list' ? 'bg-white/20 text-white ring-1 ring-white/30' : 'text-white/60 hover:text-white hover:bg-white/10'
                    ]">
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                clip-rule="evenodd"></path>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Staff Grid View -->
            <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <div v-for="(member, index) in staff.data" :key="member.id"
                    class="group relative bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 hover:bg-gray-50 animate-fade-in-up flex flex-col h-full overflow-hidden"
                    :style="{ animationDelay: `${index * 50}ms` }">
                    <!-- Photo Section -->
                    <div class="relative p-6 pb-4">
                        <div class="relative w-24 h-32 mx-auto">
                            <StaffPhoto :src="member.photo_url" :alt="member.name_en"
                                image-class="w-full h-full" />
                        </div>
                    </div>

                    <!-- Content Section -->
                    <div class="flex-1 flex flex-col px-6 pb-6">
                        <!-- Name -->
                        <h3
                            class="text-lg font-bold text-black mb-2 text-center group-hover:text-auas-600 transition-colors duration-300 line-clamp-2">
                            {{ member.name_en }}
                        </h3>

                        <!-- Title Badge -->
                        <div class="flex justify-center mb-3">
                            <span
                                class="inline-flex items-center px-3 py-1 bg-gray-100 text-black rounded-full text-xs font-medium truncate max-w-full">
                                {{ member.title_en }}
                            </span>
                        </div>

                        <!-- Department -->
                        <p class="text-gray-700 text-sm font-medium text-center mb-3 line-clamp-1">
                            {{ member.department || 'No department' }}
                        </p>

                        <!-- Specialization -->
                        <p class="text-gray-600 text-xs text-center mb-4 line-clamp-2 flex-1">
                            {{ member.specialization_en || 'No specialization specified' }}
                        </p>

                        <!-- Contact Info -->
                        <div class="space-y-2 mb-4">
                            <div class="flex items-center justify-center space-x-2">
                                <svg class="w-3 h-3 text-black flex-shrink-0" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                    </path>
                                </svg>
                                <span class="text-gray-700 text-xs truncate">{{ member.email }}</span>
                            </div>
                            <div v-if="member.phone" class="flex items-center justify-center space-x-2">
                                <svg class="w-3 h-3 text-black flex-shrink-0" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                                    </path>
                                </svg>
                                <span class="text-gray-700 text-xs truncate">{{ member.phone }}</span>
                            </div>
                            <div v-else class="flex items-center justify-center space-x-2">
                                <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                                    </path>
                                </svg>
                                <span class="text-gray-400 text-xs">No phone</span>
                            </div>
                        </div>

                        <!-- Action Button -->
                        <div class="mt-auto">
                            <Link :href="route('staff.show', member.id)"
                                class="w-full bg-gradient-to-r from-auas-600 to-auas-700 text-white px-4 py-2.5 rounded-lg hover:from-auas-700 hover:to-auas-800 transition-all duration-300 text-sm font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                                    </path>
                                </svg>
                                View Profile
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Staff List View -->
            <div v-else class="space-y-4">
                <div v-for="(member, index) in staff.data" :key="member.id"
                    class="group bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-all duration-300 hover:bg-gray-50 animate-fade-in-up overflow-hidden"
                    :style="{ animationDelay: `${index * 50}ms` }">
                    <!-- Mobile Layout (stacked vertically) -->
                    <div class="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
                        <!-- Photo and Basic Info Section -->
                        <div class="flex items-center space-x-4 sm:flex-shrink-0">
                            <!-- Photo -->
                            <div class="relative flex-shrink-0">
                                <StaffPhoto :src="member.photo_url" :alt="member.name_en"
                                    image-class="w-20 h-24 sm:w-20 sm:h-24 md:w-24 md:h-28" />
                            </div>

                            <!-- Name and Title (Mobile) -->
                            <div class="flex-1 min-w-0 sm:hidden">
                                <h3
                                    class="text-lg font-bold text-black group-hover:text-auas-600 transition-colors duration-300 line-clamp-1">
                                    {{ member.name_en }}
                                </h3>
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 bg-gray-100 text-black rounded-full text-xs font-medium mt-1 truncate max-w-full">
                                    {{ member.title_en }}
                                </span>
                            </div>
                        </div>

                        <!-- Content Section -->
                        <div class="flex-1 min-w-0 space-y-3 sm:space-y-2">
                            <!-- Desktop Name and Title -->
                            <div class="hidden sm:block">
                                <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                                    <div class="flex-1">
                                        <h3
                                            class="text-lg font-bold text-black group-hover:text-auas-600 transition-colors duration-300 break-words">
                                            {{ member.name_en }}
                                        </h3>
                                        <div
                                            class="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mt-1 space-y-1 sm:space-y-0">
                                            <span
                                                class="inline-flex items-center px-2.5 py-0.5 bg-gray-100 text-black rounded-full text-xs font-medium w-fit truncate max-w-full">
                                                {{ member.title_en }}
                                            </span>
                                            <span class="text-gray-700 text-sm font-medium truncate">{{ member.department || 'No department' }}</span>
                                        </div>
                                        <p class="text-gray-600 text-sm mt-2 line-clamp-2 sm:line-clamp-1">{{ member.specialization_en || 'No specialization specified' }}</p>
                                    </div>

                                    <!-- Desktop Action Button -->
                                    <div class="hidden lg:flex items-center space-x-2 ml-4">
                                        <Link :href="route('staff.show', member.id)"
                                            class="bg-gradient-to-r from-auas-600 to-auas-700 text-white px-4 py-2 rounded-lg hover:from-auas-700 hover:to-auas-800 transition-all duration-300 text-sm font-medium shadow-md hover:shadow-lg flex items-center whitespace-nowrap">
                                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                                                </path>
                                            </svg>
                                            View Profile
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <!-- Mobile Department and Specialization -->
                            <div class="sm:hidden space-y-2">
                                <p class="text-gray-700 text-sm font-medium truncate">{{ member.department || 'No department' }}
                                </p>
                                <p class="text-gray-600 text-sm line-clamp-2">{{ member.specialization_en || 'No specialization specified' }}</p>
                            </div>

                            <!-- Contact Info -->
                            <div
                                class="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-gray-700">
                                <div class="flex items-center space-x-2">
                                    <svg class="w-4 h-4 text-black flex-shrink-0" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z">
                                        </path>
                                    </svg>
                                    <span class="truncate">{{ member.email }}</span>
                                </div>
                                <div v-if="member.phone" class="flex items-center space-x-2">
                                    <svg class="w-4 h-4 text-black flex-shrink-0" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                                        </path>
                                    </svg>
                                    <span class="truncate">{{ member.phone }}</span>
                                </div>
                                <div v-else class="flex items-center space-x-2">
                                    <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z">
                                        </path>
                                    </svg>
                                    <span class="text-gray-400">No phone</span>
                                </div>
                            </div>

                            <!-- Mobile/Tablet Action Button -->
                            <div class="lg:hidden pt-2">
                                <Link :href="route('staff.show', member.id)"
                                    class="w-full sm:w-auto bg-gradient-to-r from-auas-600 to-auas-700 text-white px-4 py-2.5 rounded-lg hover:from-auas-700 hover:to-auas-800 transition-all duration-300 text-sm font-medium shadow-md hover:shadow-lg flex items-center justify-center">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                                        </path>
                                    </svg>
                                    View Profile
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Advanced Pagination System -->
            <div v-if="staff.total > 0" class="mt-12">
                <div class="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 overflow-hidden">
                    <!-- Mobile Results Info -->
                    <div class="lg:hidden mb-4">
                        <div class="flex flex-col space-y-3">
                            <!-- Results Summary -->
                            <div class="flex items-center justify-center space-x-2">
                                <div class="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                                <div class="text-sm text-gray-700 text-center">
                                    Showing {{ staff.from || 0 }}-{{ staff.to || 0 }} of {{ staff.total }} results
                                </div>
                            </div>

                            <!-- Page Info -->
                            <div class="flex items-center justify-center space-x-2">
                                <svg class="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                    </path>
                                </svg>
                                <div class="text-sm text-gray-700 font-medium">
                                    Page {{ staff.current_page }} of {{ staff.last_page }}
                                </div>
                            </div>

                            <!-- Page Size Selector for Mobile -->
                            <div class="flex items-center justify-center space-x-2">
                                <label class="text-sm font-medium text-black">Show:</label>
                                <select v-model="pageSize" @change="changePageSize"
                                    class="appearance-none px-3 py-1 pr-6 bg-white border border-gray-300 rounded-lg text-sm font-medium text-black focus:outline-none focus:ring-2 focus:ring-auas-300 focus:border-transparent transition-all duration-200 [&>option]:text-black [&>option]:bg-white">
                                    <option v-for="option in pageSizeOptions" :key="option.value" :value="option.value"
                                        class="text-black bg-white">
                                        {{ option.mobileLabel }}
                                    </option>
                                </select>
                            </div>

                            <!-- Loading Indicator -->
                            <div v-if="isSearching"
                                class="flex items-center justify-center space-x-2 text-xs text-gray-600">
                                <div
                                    class="w-3 h-3 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin">
                                </div>
                                <span>Loading...</span>
                            </div>
                        </div>
                    </div>

                    <!-- Desktop Pagination Header -->
                    <div class="hidden lg:flex lg:items-center lg:justify-between mb-6">
                        <!-- Enhanced Results Info -->
                        <div class="flex items-center space-x-4">
                            <div class="flex items-center space-x-2">
                                <div class="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
                                <div class="text-sm text-gray-700">
                                    Showing
                                    <span class="font-semibold text-black bg-gray-100 px-2 py-1 rounded">{{ staff.from
                                        || 0 }}</span>
                                    to
                                    <span class="font-semibold text-black bg-gray-100 px-2 py-1 rounded">{{ staff.to ||
                                        0 }}</span>
                                    of
                                    <span class="font-semibold text-black bg-gray-100 px-2 py-1 rounded">{{ staff.total
                                    }}</span>
                                    results
                                </div>
                            </div>
                            <div class="w-1 h-4 bg-gradient-to-b from-auas-300 to-auas-400 rounded-full"></div>
                            <div class="flex items-center space-x-2">
                                <svg class="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                                    </path>
                                </svg>
                                <div class="text-sm text-gray-700 font-medium">
                                    Page {{ staff.current_page }} of {{ staff.last_page }}
                                </div>
                            </div>
                            <!-- Loading Time Indicator -->
                            <div v-if="isSearching" class="flex items-center space-x-2 text-xs text-gray-600">
                                <div
                                    class="w-3 h-3 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin">
                                </div>
                                <span>Loading...</span>
                            </div>
                        </div>

                        <!-- Enhanced Page Size Selector -->
                        <div class="flex items-center space-x-3">
                            <label class="text-sm font-medium text-black flex items-center space-x-1">
                                <svg class="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
                                </svg>
                                <span>Show:</span>
                            </label>
                            <div class="relative">
                                <select v-model="pageSize" @change="changePageSize"
                                    class="appearance-none px-4 py-2 pr-8 bg-white border border-gray-300 rounded-lg text-sm font-medium text-black focus:outline-none focus:ring-2 focus:ring-auas-300 focus:border-transparent transition-all duration-200 hover:border-gray-400 cursor-pointer [&>option]:text-black [&>option]:bg-white">
                                    <option v-for="option in pageSizeOptions" :key="option.value" :value="option.value"
                                        class="text-black bg-white">
                                        {{ option.label }}
                                    </option>
                                </select>
                                <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <svg class="w-4 h-4 text-black" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile Simple Navigation -->
                    <div class="lg:hidden">
                        <div class="flex items-center justify-between">
                            <Link v-if="staff.prev_page_url" :href="staff.prev_page_url" :only="partialReloadOnly"
                                preserve-state preserve-scroll
                                class="flex items-center px-4 py-2 text-sm font-medium text-black bg-white rounded-lg hover:bg-gray-100 border border-gray-300 transition-all duration-200">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 19l-7-7 7-7"></path>
                                </svg>
                                Previous
                            </Link>
                            <button v-else disabled
                                class="flex items-center px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed">
                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 19l-7-7 7-7"></path>
                                </svg>
                                Previous
                            </button>

                            <div class="text-sm text-gray-700 font-medium">
                                {{ staff.current_page }} / {{ staff.last_page }}
                            </div>

                            <Link v-if="staff.next_page_url" :href="staff.next_page_url" :only="partialReloadOnly"
                                preserve-state preserve-scroll
                                class="flex items-center px-4 py-2 text-sm font-medium text-black bg-white rounded-lg hover:bg-gray-100 border border-gray-300 transition-all duration-200">
                                Next
                                <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 5l7 7-7 7"></path>
                                </svg>
                            </Link>
                            <button v-else disabled
                                class="flex items-center px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed">
                                Next
                                <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Desktop Full Pagination Controls -->
                    <div class="hidden lg:flex lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                        <!-- Previous/Next Navigation -->
                        <div class="flex items-center space-x-2">
                            <Link v-if="staff.prev_page_url" :href="staff.prev_page_url" :only="partialReloadOnly"
                                preserve-state preserve-scroll
                                class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-black hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 group">
                                <svg class="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" fill="none"
                                    stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 19l-7-7 7-7"></path>
                                </svg>
                                Previous
                            </Link>
                            <button v-else disabled
                                class="inline-flex items-center px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-400 cursor-not-allowed">
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 19l-7-7 7-7"></path>
                                </svg>
                                Previous
                            </button>

                            <Link v-if="staff.next_page_url" :href="staff.next_page_url" :only="partialReloadOnly"
                                preserve-state preserve-scroll
                                class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-black hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 group">
                                Next
                                <svg class="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none"
                                    stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 5l7 7-7 7"></path>
                                </svg>
                            </Link>
                            <button v-else disabled
                                class="inline-flex items-center px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-400 cursor-not-allowed">
                                Next
                                <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </div>

                        <!-- Page Numbers (Desktop Only) -->
                        <div class="hidden lg:flex items-center justify-center">
                            <nav class="flex items-center space-x-1" v-if="staff.last_page > 1">
                                <!-- First Page -->
                                <Link v-if="staff.current_page > 3" :href="route('staff.index')" :data="{
                                    ...searchForm,
                                    per_page: pageSize,
                                    page: 1
                                }" :only="partialReloadOnly" preserve-state preserve-scroll
                                    class="px-3 py-2 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-all duration-200">
                                    1
                                </Link>
                                <span v-if="staff.current_page > 4" class="px-2 py-2 text-gray-600">...</span>

                                <!-- Page Numbers -->
                                <template v-for="page in getVisiblePages()" :key="page">
                                    <Link v-if="page !== staff.current_page" :href="route('staff.index')" :data="{
                                        ...searchForm,
                                        per_page: pageSize,
                                        page: page
                                    }" :only="partialReloadOnly" preserve-state preserve-scroll
                                        class="px-3 py-2 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 transition-all duration-200">
                                        {{ page }}
                                    </Link>
                                    <span v-else
                                        class="px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-auas-500 to-auas-600 rounded-lg shadow-lg">
                                        {{ page }}
                                    </span>
                                </template>

                                <!-- Last Page -->
                                <span v-if="staff.current_page < staff.last_page - 3"
                                    class="px-2 py-2 text-gray-600">...</span>
                                <Link v-if="staff.current_page < staff.last_page - 2" :href="route('staff.index')"
                                    :data="{
                                        ...searchForm,
                                        per_page: pageSize,
                                        page: staff.last_page
                                    }" :only="partialReloadOnly" preserve-state preserve-scroll
                                    class="px-3 py-2 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-all duration-200">
                                    {{ staff.last_page }}
                                </Link>
                            </nav>
                        </div>

                        <!-- Enhanced Jump to Page with Keyboard Navigation (Desktop Only) -->
                        <div class="hidden lg:flex items-center space-x-3">
                            <label class="text-sm font-medium text-black flex items-center space-x-1">
                                <svg class="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M13 9l3 3-3 3m-6 0l3-3-3-3"></path>
                                </svg>
                                <span>Jump to:</span>
                            </label>
                            <div class="flex items-center space-x-2">
                                <div class="relative">
                                    <input v-model="jumpToPage" type="number" :min="1" :max="staff.last_page"
                                        placeholder="Page"
                                        class="w-20 px-3 py-2 text-sm bg-white border border-gray-300 text-black rounded-lg focus:outline-none focus:ring-2 focus:ring-auas-300 focus:border-transparent transition-all duration-200 text-center placeholder-gray-400"
                                        @keyup.enter="goToPage" @keyup.escape="jumpToPage = ''" />
                                    <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                        <span class="text-xs text-gray-600">/{{ staff.last_page }}</span>
                                    </div>
                                </div>
                                <button @click="goToPage"
                                    :disabled="!jumpToPage || jumpToPage < 1 || jumpToPage > staff.last_page"
                                    class="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-auas-500 to-auas-600 rounded-lg hover:from-auas-600 hover:to-auas-700 disabled:from-gray-200 disabled:to-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 5l7 7-7 7"></path>
                                    </svg>
                                </button>
                            </div>

                            <!-- Quick Navigation Buttons -->
                            <div class="hidden lg:flex items-center space-x-1 ml-4 pl-4 border-l border-gray-300">
                                <button @click="goToFirstPage" :disabled="staff.current_page === 1"
                                    class="p-2 text-black hover:text-auas-600 hover:bg-gray-100 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="First Page (Ctrl+Home)">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7">
                                        </path>
                                    </svg>
                                </button>
                                <button @click="goToLastPage" :disabled="staff.current_page === staff.last_page"
                                    class="p-2 text-black hover:text-auas-600 hover:bg-gray-100 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Last Page (Ctrl+End)">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M13 5l7 7-7 7M5 5l7 7-7 7">
                                        </path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <br>
            </div>

            <!-- No Results -->
            <div v-if="staff.data.length === 0" class="text-center py-12">
                <div class="text-white/70">
                    <svg class="mx-auto h-12 w-12 text-white/60 mb-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 class="text-lg font-medium text-white mb-2">No staff members found</h3>
                    <p class="text-white/60">Try adjusting your search criteria.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { debounce } from 'lodash'
import StaffPhoto from '@/components/StaffPhoto.vue'
import { globalImageCache } from '@/composables/useImageCache.js'

const props = defineProps({
    staff: Object,
    colleges: Array,
    departments: Array,
    departmentsByCollege: Object, // Departments grouped by college for cascading dropdown
    filters: Object
})

// Reactive state
const searchForm = reactive({
    search: props.filters.search || '',
    college: props.filters.college || '',
    department: props.filters.department || '',
    title: props.filters.title || '',
    specialization: props.filters.specialization || '',
    sort: props.filters.sort || 'rank',
    letter: props.filters.letter || ''
})

const isSearching = ref(false)
const viewMode = ref('grid')
const showAdvancedFilters = ref(false)
const partialReloadOnly = ['staff', 'filters']

// Alphabet for letter filtering
const alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']

// Pagination state
const pageSize = ref(String(props.filters.per_page || 25))
const jumpToPage = ref('')

// Computed properties
const hasActiveFilters = computed(() => {
    return searchForm.search ||
        searchForm.college ||
        searchForm.department ||
        searchForm.title ||
        searchForm.specialization ||
        searchForm.letter ||
        searchForm.sort !== 'rank'
})

const pageSizeOptions = computed(() => [
    { value: '5', label: '5 per page', mobileLabel: '5' },
    { value: '10', label: '10 per page', mobileLabel: '10' },
    { value: '25', label: '25 per page', mobileLabel: '25' },
    { value: '50', label: '50 per page', mobileLabel: '50' },
    { value: '100', label: '100 per page', mobileLabel: '100' },
    {
        value: 'all',
        label: `All ${props.staff.total} staff`,
        mobileLabel: `All ${props.staff.total}`,
    },
])

// Methods
const visitDirectory = (data, options = {}) => {
    isSearching.value = true
    const { onFinish, ...visitOptions } = options

    router.get(route('staff.index'), data, {
        only: partialReloadOnly,
        preserveState: true,
        preserveScroll: true,
        ...visitOptions,
        onFinish: () => {
            isSearching.value = false
            onFinish?.()
        }
    })
}

const search = () => {
    visitDirectory({
        ...searchForm,
        per_page: pageSize.value
    }, {
        replace: true,
    })
}

const debouncedSearch = debounce(() => {
    search()
}, 300)

// Cascading dropdown logic
const availableDepartments = computed(() => {
    if (!searchForm.college) {
        return [] // Return empty array when no college is selected
    }

    // Use departmentsByCollege map if available
    if (props.departmentsByCollege && props.departmentsByCollege[searchForm.college]) {
        return props.departmentsByCollege[searchForm.college]
    }

    return []
})

const onCollegeChange = () => {
    // Clear department filter when college changes
    searchForm.department = ''
    search()
}

const onDepartmentChange = () => {
    search()
}

const getCollegeStaffCount = (collegeId) => {
    if (!collegeId) return props.staff.total
    // Use the active_staff_count directly from the college object
    const college = props.colleges.find(col => col.id === collegeId)
    return college?.active_staff_count || 0
}

const getSelectedCollegeTotalStaff = () => {
    if (!searchForm.college) return props.staff.total
    return getCollegeStaffCount(searchForm.college)
}

const clearFilter = (filterName) => {
    searchForm[filterName] = ''
    search()
}

const clearAllFilters = () => {
    Object.keys(searchForm).forEach(key => {
        if (key === 'sort') {
            searchForm[key] = 'rank'
        } else {
            searchForm[key] = ''
        }
    })
    pageSize.value = '25'
    jumpToPage.value = ''
    isSearching.value = true
    router.visit(route('staff.index'), {
        method: 'get',
        replace: true,
        preserveState: false,
        preserveScroll: false,
        onFinish: () => {
            isSearching.value = false
        }
    })
}

// Letter filtering methods
const selectLetter = (letter) => {
    searchForm.letter = searchForm.letter === letter ? '' : letter
    search()
}

const clearLetter = () => {
    searchForm.letter = ''
    search()
}

// Enhanced Pagination methods
const changePageSize = () => {
    visitDirectory({
        ...searchForm,
        per_page: pageSize.value,
        page: 1 // Reset to first page when changing page size
    }, {
        preserveScroll: false,
    })
}

const goToPage = () => {
    if (jumpToPage.value && jumpToPage.value >= 1 && jumpToPage.value <= props.staff.last_page) {
        visitDirectory({
            ...searchForm,
            per_page: pageSize.value,
            page: jumpToPage.value
        }, {
            preserveScroll: false,
        })
        jumpToPage.value = ''
    }
}

const goToFirstPage = () => {
    if (props.staff.current_page !== 1) {
        visitDirectory({
            ...searchForm,
            per_page: pageSize.value,
            page: 1
        }, {
            preserveScroll: false
        })
    }
}

const goToLastPage = () => {
    if (props.staff.current_page !== props.staff.last_page) {
        visitDirectory({
            ...searchForm,
            per_page: pageSize.value,
            page: props.staff.last_page
        }, {
            preserveScroll: false
        })
    }
}

// Keyboard navigation for pagination
const handleKeyboardNavigation = (event) => {
    if (event.ctrlKey) {
        switch (event.key) {
            case 'Home':
                event.preventDefault()
                goToFirstPage()
                break
            case 'End':
                event.preventDefault()
                goToLastPage()
                break
            case 'ArrowLeft':
                event.preventDefault()
                if (props.staff.prev_page_url) {
                    router.visit(props.staff.prev_page_url, {
                        only: partialReloadOnly,
                        preserveState: true,
                        preserveScroll: false,
                    })
                }
                break
            case 'ArrowRight':
                event.preventDefault()
                if (props.staff.next_page_url) {
                    router.visit(props.staff.next_page_url, {
                        only: partialReloadOnly,
                        preserveState: true,
                        preserveScroll: false,
                    })
                }
                break
        }
    }
}



const getVisiblePages = () => {
    const current = props.staff.current_page
    const last = props.staff.last_page
    const pages = []

    // Show current page and 2 pages on each side
    const start = Math.max(1, current - 2)
    const end = Math.min(last, current + 2)

    for (let i = start; i <= end; i++) {
        pages.push(i)
    }

    return pages
}

// Lifecycle
onMounted(() => {
    // Add some entrance animations
    const cards = document.querySelectorAll('.animate-fade-in-up')
    cards.forEach((card, index) => {
        setTimeout(() => {
            card.classList.add('opacity-100', 'translate-y-0')
        }, index * 100)
    })

    // Add keyboard navigation listeners
    document.addEventListener('keydown', handleKeyboardNavigation)

    // Preload staff images for better performance
    if (props.staff?.data?.length > 0) {
        globalImageCache.smartPreload(props.staff.data, 'down')
    }
})

// Watch for staff data changes to preload new images
watch(() => props.staff?.data, (newStaffData) => {
    if (newStaffData?.length > 0) {
        globalImageCache.smartPreload(newStaffData, 'down')
    }
}, { immediate: false })

onUnmounted(() => {
    // Clean up keyboard navigation listeners
    document.removeEventListener('keydown', handleKeyboardNavigation)
})
</script>

<style scoped>
/* Custom Animations */
@keyframes float {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    33% {
        transform: translateY(-10px) rotate(1deg);
    }

    66% {
        transform: translateY(5px) rotate(-1deg);
    }
}

@keyframes float-delayed {

    0%,
    100% {
        transform: translateY(0px) rotate(0deg);
    }

    33% {
        transform: translateY(8px) rotate(-1deg);
    }

    66% {
        transform: translateY(-12px) rotate(1deg);
    }
}

@keyframes pulse-slow {

    0%,
    100% {
        opacity: 0.3;
        transform: scale(1);
    }

    50% {
        opacity: 0.8;
        transform: scale(1.05);
    }
}

@keyframes fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes fade-in-up {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes gradient-shift {

    0%,
    100% {
        background-position: 0% 50%;
    }

    50% {
        background-position: 100% 50%;
    }
}

/* Animation Classes */
.animate-float {
    animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
    animation: float-delayed 8s ease-in-out infinite;
}

.animate-pulse-slow {
    animation: pulse-slow 4s ease-in-out infinite;
}

.animate-fade-in {
    animation: fade-in 0.5s ease-out;
}

.animate-fade-in-up {
    opacity: 0;
    transform: translateY(30px);
    animation: fade-in-up 0.6s ease-out forwards;
}

/* Glassmorphism Effects */
.backdrop-blur-xl {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

/* Custom Utilities */
.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Hover Effects */
.group:hover .group-hover\:shadow-xl {
    box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
}

/* Gradient Text */
.bg-clip-text {
    -webkit-background-clip: text;
    background-clip: text;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: rgb(0 0 0 / 0.1);
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(45deg, rgb(var(--color-primary-500)), rgb(var(--color-primary-600)));
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(45deg, rgb(var(--color-primary-600)), rgb(var(--color-primary-700)));
}

/* Focus States */
.focus\:ring-primary-500\/50:focus {
    --tw-ring-color: rgb(var(--color-primary-500) / 0.5);
}

/* Transform Utilities */
.hover\:-translate-y-0\.5:hover {
    transform: translateY(-2px);
}

/* Border Utilities */
.border-3 {
    border-width: 3px;
}

/* Advanced Filter Animation */
.animate-fade-in {
    animation: fade-in 0.3s ease-out;
}

/* Loading Animation */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}

/* Responsive Design Enhancements */
@media (max-width: 768px) {
    .animate-fade-in-up {
        animation-delay: 0ms !important;
    }
}

/* Dark Mode Support (Future Enhancement) */
@media (prefers-color-scheme: dark) {
    .bg-white\/80 {
        background-color: rgb(var(--color-academic-800) / 0.8);
    }
}
</style>
